/* eslint-disable max-len */
import React, { useState } from 'react';
import styled from 'styled-components';
import { withFormik } from 'formik';
import { Column } from 'rbx';
import * as yup from 'yup';
import swal from 'sweetalert';
import { trim, first } from 'lodash';

import { FaAngleLeft } from 'react-icons/fa';
import { Input, CheckBox, Select } from '../../../../components/elements';
import LocationSuggestion from '../../../../components/global/GoogleSuggest';

const CheckBoxWrapper = styled.div`
  padding-top: 2rem;
`;

const TIME_ZONE_OPTIONS = [
  { value: '+10', label: 'AEST' },
  { value: '+8', label: 'AWST' },
  { value: '+9.5', label: 'ACST' },
  { value: '+5.5', label: 'IST' },
];

const Container = styled.form`
  &&& {
    .modal-background {
      background-color: rgba(13, 13, 13, 0.28);
    }
    .modal-card-body {
      height: 100vh;
      padding: 0px;
    }
    .modal-card {
      max-height: calc(100vh - 0px);
      position: absolute;
      right: 0;
      width: 35rem;
    }
    .modal-card-head,
    .modal-card-foot {
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    }
    .modal-card-title {
      font-size: 1.5rem;
      font-weight: 600;
    }
    .modal-card-head {
      border-bottom: 0px;
      padding: 1rem 2rem;
      padding-top: 1.5rem;
      background: white;
    }
    .modal-card-foot {
      justify-content: center;
      background: white;
      border-top: 0px;
      .button {
        font-weight: 600;
      }
    }
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  left: 1rem;
  top: 1rem;
`;

const DetailsFormWrapper = styled.div`
  padding: 1rem 2rem;
`;

const removeTypename = obj => {
  const temp = { ...obj };
  // eslint-disable-next-line no-underscore-dangle
  delete temp.__typename;

  return temp;
};

const Form = ({
  isActive,
  onClose,
  handleSubmit,
  resetForm,
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errors,
  touched,
  adminUpdateMenuOrderLoading,
}) => {
  const [isManual, setManual] = useState(false);
  const timeZoneLabel = first(
    TIME_ZONE_OPTIONS.filter(item => item.value === values.timezone),
  );

  return (
    <Container className={`modal ${isActive && 'is-active'}`}>
      <div className="modal-background" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p
            className="modal-card-title is-flex"
            style={{ justifyContent: 'center' }}
          >
            Update Order
          </p>

          <CloseButtonWrapper>
            <FaAngleLeft
              size={30}
              type="button"
              onClick={() => {
                onClose();
                resetForm();
              }}
            />
          </CloseButtonWrapper>
        </header>

        <section className="modal-card-body">
          <DetailsFormWrapper>
            <Column.Group multiline>
              <Column size={12}>
                <LocationSuggestion
                  label="Search Address"
                  initialValue={values.city}
                  onSelect={place => {
                    setFieldValue('address_line_1', trim(place.addressLine1));
                    setFieldValue('city', place.city);
                    setFieldValue('post_code', place.postcode);
                    setFieldValue('state', place.state);
                    setFieldValue('country', place.country || 'AUSTRALIA');
                    setFieldValue('latitude', place.latitude);
                    setFieldValue('longitude', place.longitude);
                  }}
                  disabled={isManual}
                />
              </Column>
              <Column size={12}>
                <CheckBoxWrapper>
                  <CheckBox
                    label="Override Location"
                    value={isManual}
                    onChange={() => setManual(!isManual)}
                  />
                </CheckBoxWrapper>
              </Column>
              <Column size={12}>
                <Input
                  label="Type"
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.type}
                  touched={touched.type}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Input
                  label="Address Line 1"
                  name="address_line_1"
                  value={values.address_line_1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.address_line_1}
                  touched={touched.address_line_1}
                  disabled={!isManual}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Input
                  label="Address Line 2"
                  name="address_line_2"
                  value={values.address_line_2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.address_line_2}
                  touched={touched.address_line_2}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Input
                  label="City"
                  name="city"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.city}
                  touched={touched.city}
                  disabled={!isManual}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Input
                  label="State"
                  name="state"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.state}
                  touched={touched.state}
                  disabled={!isManual}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Input
                  label="Post Code"
                  name="post_code"
                  value={values.post_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.post_code}
                  touched={touched.post_code}
                  disabled={!isManual}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Input
                  label="Country"
                  name="country"
                  value={values.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors.country}
                  touched={touched.country}
                  disabled={!isManual}
                  disableToastMessage
                />
              </Column>
              <Column size={12}>
                <Select
                  label="TimeZone"
                  disabled={!isManual}
                  options={TIME_ZONE_OPTIONS}
                  value={[
                    {
                      value: values.timezone,
                      label:
                        timeZoneLabel && timeZoneLabel.length !== 0
                          ? timeZoneLabel.label
                          : 'AEST',
                    },
                  ]}
                  onChange={({ value }) => setFieldValue('timezone', value)}
                  errors={errors.timezone}
                  touched={touched.timezone}
                />
              </Column>
              <Column size={6}>
                <Input
                  label="Latitude"
                  type="number"
                  name="latitude"
                  value={values.latitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!isManual}
                  errors={errors.latitude}
                  touched={touched.latitude}
                  disableToastMessage
                />
              </Column>
              <Column size={6}>
                <Input
                  label="Longitude"
                  type="number"
                  name="longitude"
                  value={values.longitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!isManual}
                  errors={errors.longitude}
                  touched={touched.longitude}
                  disableToastMessage
                />
              </Column>
            </Column.Group>
          </DetailsFormWrapper>
        </section>
        <footer className="modal-card-foot">
          <button
            className={`button is-large is-fullwidth is-primary ${adminUpdateMenuOrderLoading &&
              'is-loading'}`}
            onClick={handleSubmit}
          >
            UPDATE ORDER
          </button>
        </footer>
      </div>
    </Container>
  );
};

const DeliveryAddressUpdate = withFormik({
  mapPropsToValues: ({ order }) => ({
    type: order?.address?.type,
    address_line_1: order?.address?.address_line_1,
    address_line_2: order?.address?.address_line_2,
    city: order?.address?.city,
    state: order?.address?.state,
    post_code: order?.address?.post_code,
    country: order?.address?.country,
    timezone: order?.address?.timezone || '+10',
    latitude: order?.address?.latitude,
    longitude: order?.address?.longitude,
  }),

  validationSchema: yup.object().shape({}),

  handleSubmit: (values, { props }) => {
    swal({
      title: 'Are you sure to update the order?',
      buttons: ['Cancel', 'Confirm'],
    }).then(async isConfirm => {
      if (isConfirm) {
        await props.adminUpdateMenuOrder({
          variables: {
            input: {
              menu_order_id: props.order.menu_order_id,
              user_id: props.userId,
              address: {
                ...removeTypename(props?.order?.address ?? {}),
                type: values.type,
                address_line_1: values.address_line_1,
                address_line_2: values.address_line_2,
                city: values.city,
                state: values.state,
                post_code: values.post_code,
                country: values.country,
                timezone: values.timezone,
                latitude: values.latitude,
                longitude: values.longitude,
                delivery_option: {
                  type: 'DELIVERY_TO_DOOR',
                  caption: null,
                },
              },
            },
          },
        });
      }
    });
  },

  displayName: 'DeliveryAddressUpdate',
})(Form);

export default DeliveryAddressUpdate;
